import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import BackButton from '../../components/common/BackButton';

const PLATFORMS = [
  {
    key: 'marketplace',
    href: 'https://admin-marketplace.devskin.com',
    gradient: 'from-green-500 to-emerald-600',
    bgGradient: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
    borderColor: 'border-green-200 dark:border-green-800/50',
    icon: '🏪',
  },
  {
    key: 'marketing',
    href: 'https://admin-marketing.devskin.com',
    gradient: 'from-orange-500 to-amber-500',
    bgGradient: 'from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20',
    borderColor: 'border-orange-200 dark:border-orange-800/50',
    icon: '📢',
  },
  {
    key: 'crm',
    href: 'https://admin-crm.devskin.com',
    gradient: 'from-red-500 to-rose-600',
    bgGradient: 'from-red-50 to-rose-50 dark:from-red-900/20 dark:to-rose-900/20',
    borderColor: 'border-red-200 dark:border-red-800/50',
    icon: '👥',
  },
  {
    key: 'analytics',
    href: 'https://admin-flux.kubmix.com',
    gradient: 'from-blue-500 to-indigo-600',
    bgGradient: 'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800/50',
    icon: '📊',
    isCurrent: true,
  },
  {
    key: 'digitalSign',
    href: 'https://admin-sign.devskin.com',
    gradient: 'from-violet-500 to-purple-600',
    bgGradient: 'from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20',
    borderColor: 'border-violet-200 dark:border-violet-800/50',
    icon: '🖥️',
  },
  {
    key: 'cloud',
    href: 'https://admin-cloud.kubmix.com',
    gradient: 'from-sky-500 to-sky-600',
    bgGradient: 'from-sky-50 to-sky-50 dark:from-sky-900/20 dark:to-sky-900/20',
    borderColor: 'border-sky-200 dark:border-sky-800/50',
    icon: '☁️',
  },
];

export default function PlatformsPage() {
  const { t } = useTranslation(['settings', 'common']);

  return (
    <MainLayout>
      <BackButton />
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('settings:platforms.title')}</h1>
        <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">{t('settings:platforms.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        {PLATFORMS.map((platform) => (
          <div
            key={platform.key}
            className={`relative overflow-hidden rounded-xl sm:rounded-2xl border ${platform.borderColor} bg-gradient-to-br ${platform.bgGradient} transition-all duration-300 flex flex-col ${
              platform.isCurrent ? 'ring-2 ring-blue-400/50 dark:ring-blue-500/30' : 'hover:shadow-lg hover:-translate-y-0.5'
            }`}
          >
            <div className="p-4 sm:p-6 flex-1">
              <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className={`p-2.5 sm:p-3 rounded-xl bg-gradient-to-br ${platform.gradient} text-white shadow-lg text-xl sm:text-2xl flex items-center justify-center w-11 h-11 sm:w-14 sm:h-14 shrink-0`}>
                  {platform.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate">
                    {t(`settings:platforms.items.${platform.key}.name`)}
                  </h3>
                  {platform.isCurrent && (
                    <span className="inline-flex items-center gap-1 mt-0.5 text-[11px] font-semibold text-blue-700 dark:text-blue-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                      {t('settings:platforms.currentPlatform')}
                    </span>
                  )}
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3 sm:mb-4">
                {t(`settings:platforms.items.${platform.key}.description`)}
              </p>
              {!platform.isCurrent && (
                <a
                  href={platform.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 transition-colors shadow-sm w-full sm:w-auto justify-center sm:justify-start"
                >
                  {t('settings:platforms.openPlatform')}
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H7M17 7v10" /></svg>
                </a>
              )}
            </div>
            <div className={`h-1 bg-gradient-to-r ${platform.gradient}`} />
          </div>
        ))}
      </div>

      <div className="mt-4 sm:mt-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 sm:p-5">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="p-2 sm:p-2.5 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 text-lg sm:text-xl shrink-0">
            🔗
          </div>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed pt-0.5 sm:pt-1">
            {t('settings:platforms.integratedEcosystem')}
          </p>
        </div>
      </div>
    </MainLayout>
  );
}
